import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Box,
  Avatar,
  Card,
  CardContent,
  Grid,
  Button,
} from '@mui/material';
import {
  Person,
  Hotel,
  MeetingRoom,
} from '@mui/icons-material';
import { appointmentService } from '../../services/api';
import TopBar from '../../components/TopBar';

const IPDPage = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);

  useEffect(() => {
    loadIPDPatients();
  }, []);

  const loadIPDPatients = async () => {
    try {
      const response = await appointmentService.getAllAppointments();
      const ipdAppointments = response.data.filter(
        (apt) => apt.type === 'IPD' && apt.status === 'Scheduled'
      );
      setAppointments(ipdAppointments);
    } catch (error) {
      console.error('Error loading IPD patients:', error);
    }
  };

  const handleSelectPatient = (patientId) => {
    navigate(`/patient/${patientId}`);
  };

  const handleDischarge = (appointmentId) => {
    navigate(`/ipd/discharge/${appointmentId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="IPD - Inpatient Department" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Hotel sx={{ fontSize: 40, color: '#9c27b0', mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Admitted Patients
            </Typography>
          </Box>

          {appointments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No patients currently admitted
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {appointments.map((appointment) => (
                <Grid item xs={12} sm={6} md={4} key={appointment.id}>
                  <Card
                    className="card-hover"
                    sx={{
                      height: '100%',
                      borderLeft: '4px solid #9c27b0',
                      '&:hover': { boxShadow: '0 8px 20px rgba(156, 39, 176, 0.2)' },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#9c27b0', mr: 2 }}>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {appointment.patient?.firstName} {appointment.patient?.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Patient ID: {appointment.patientId}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Admission Date:</strong>{' '}
                          {new Date(appointment.appointmentDate).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Age:</strong> {appointment.patient?.age || 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Gender:</strong> {appointment.patient?.gender || 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Blood Group:</strong>{' '}
                          {appointment.patient?.bloodGroup || 'N/A'}
                        </Typography>
                        {appointment.notes && (
                          <Typography variant="body2" sx={{ mt: 2 }}>
                            <strong>Notes:</strong> {appointment.notes}
                          </Typography>
                        )}
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        sx={{ mt: 2, bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}
                        onClick={() => handleSelectPatient(appointment.patientId)}
                      >
                        View Patient Details
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        startIcon={<MeetingRoom />}
                        sx={{ mt: 1 }}
                        onClick={() => handleDischarge(appointment.id)}
                      >
                        Discharge Patient
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default IPDPage;
